import React, {
  lazy,
  Suspense,
  useState,
  useMemo,
  useEffect,
  useRef,
} from "react";
import cn from "classnames";
import {
  api,
  genresApi,
  useAppDispatch,
  useAppSelector,
} from "@litres-core/store";
import type { IGenre } from "@litres-core/store";
import { useTranslation } from "@litres-libs/i18n";
import { SpriteIcon } from "@litres-libs/sprite-icons";
import { StyledLink } from "@litres/ui-kit/link";
import { SkeletonBlock } from "@litres/ui-kit/skeleton";
import { EAvailableFaceLanguages, transformGenresData } from "@litres/utils";

import type { GenresTreePopupProps } from "./types";
import { AllGenres } from "./AllGenres/AllGenres";
import { GENRES } from "./constants";
import styles from "./GenresTreePopup.module.scss";

const GenrePreview = lazy(() => import("./GenrePreview/GenrePreview"));

export const GenresTreePopup = (props: GenresTreePopupProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    externalRef,
    genresButtonRef,
    className,
    hasOverlay,
    style,
    ...otherProps
  } = props;
  const [isPreview, setPreviewVisibility] = useState<boolean>(false);
  const disableClose = useRef(false);
  const {
    popup: { isOpen },
  } = useAppSelector(state => state.global.genres);

  const faceLang = useAppSelector(
    state => state.face.language || EAvailableFaceLanguages.ru
  );

  const { data: genresList, isFetching } = genresApi.useGetAllGenresQuery(
    {
      subgenre_depth: 3,
    },
    { skip: !isOpen }
  );
  const dispatch = useAppDispatch();
  const showPreview = (value: boolean) => {
    disableClose.current = true;
    setPreviewVisibility(value);
    setTimeout(() => (disableClose.current = false), 1000);
  };
  const closeHandler = () => {
    dispatch(api.global.genres.setOpenPopup(false));
  };
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      closeHandler();
    }
  };
  const focusOutHandler = (e: FocusEvent) => {
    // https://stackoverflow.com/questions/66047896/event-listener-which-detects-that-focus-within-lost-focus
    const self = e.currentTarget as HTMLElement;
    /*
        If the document has lost focus,
        don't hide the container just yet,
        wait until the focus is returned.
     */
    if (!document.hasFocus()) {
      window.addEventListener("focus", function focusReturn() {
        /*
            We want the listener to be triggered just once,
            so we have it remove itself from the `focus` event.
        */
        window.removeEventListener("focus", focusReturn);

        /*
            Test whether the active element
            is within our container.
         */
        if (!self.contains(document.activeElement) && !disableClose.current) {
          closeHandler();
        }
      });
      return;
    }
    if (
      !self.contains(e.relatedTarget as HTMLElement) &&
      !disableClose.current &&
      genresButtonRef?.current !== e.relatedTarget
    ) {
      closeHandler();
    }
  };
  const genres = useMemo(() => {
    if (genresList) {
      return transformGenresData(genresList) as IGenre[];
    }
    // GENRES - fallback для SEO
    return transformGenresData(GENRES, true, faceLang) as IGenre[];
  }, [genresList]);

  useEffect(() => {
    const originalStyle = document.body.style.overflow;
    const activeEl = document.activeElement as HTMLElement;

    if (isOpen && externalRef?.current) {
      externalRef.current.focus();
      externalRef.current.addEventListener("focusout", focusOutHandler);
    }

    if (hasOverlay) {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = originalStyle;
      }
    }

    document.addEventListener("keydown", onKeyDown);

    return () => {
      if (activeEl) {
        activeEl.focus();
      }
      if (externalRef?.current) {
        externalRef.current.removeEventListener("focusout", focusOutHandler);
      }
      document.body.style.overflow = originalStyle;
      document.removeEventListener("keydown", onKeyDown);
      if (isPreview) {
        setPreviewVisibility(false);
      }
      dispatch(api.global.genres.setCurrentGenre(null));
      dispatch(api.global.genres.setHistory([]));
    };
  }, [hasOverlay, isOpen]);

  return (
    <>
      <div
        tabIndex={0}
        data-header-popup="genres"
        className={cn(styles.wrapper, className, {
          [styles.hidden]: !isOpen,
        })}
        ref={externalRef}
        style={style}
        {...otherProps}
      >
        <div className={styles.container}>
          <div className={styles.content}>
            {isPreview ? (
              <Suspense fallback={<SkeletonBlock height="100%" width="100%" />}>
                <GenrePreview showPreview={showPreview} />
              </Suspense>
            ) : (
              <AllGenres
                genres={genres}
                isLoading={isFetching}
                isOpen={isOpen}
                isPreview={isPreview}
                showPreview={showPreview}
              />
            )}
          </div>
        </div>
        <div className={styles["all-genres"]}>
          <StyledLink href="/pages/new_genres/">
            {t("Все {count} жанр", { count: 142 })}
            <SpriteIcon name="chevronRight" size={24} />
          </StyledLink>
        </div>
      </div>
      {isOpen && hasOverlay && (
        <div
          className={styles.overlay}
          style={{ top: style?.top ?? undefined }}
        />
      )}
    </>
  );
};

export default GenresTreePopup;
